<template lang="pug">
.custom-field-edit
  div(v-if="!isLoading")
    UpsertPage(@save="save" @cancel="toOverview" :pending="isSaving" :canSave="canSave" :title="title")
      BriqPanel
        BriqInput(
          label="Name" 
          v-model="fieldName" 
          :disabled="isSaving" 
          :error="showError('fieldName', 'Display Name')" 
          @blur="$v.fieldName.$touch()" 
          maxlength="100" 
          required
          data-test-id="custom-field-name"
        )
        BriqInput(
          label="Code" 
          v-model="fieldCode" 
          :disabled="isSaving" 
          help="Only letters, numbers and underscore are allowed"
          :error="showError('fieldCode', 'Code')" 
          @blur="$v.fieldCode.$touch()" 
          maxlength="100" 
          required
          data-test-id="code"
        )
        BriqTextarea(label="Description" v-model="description" :disabled="isSaving" data-test-id="custom-field-description")
        BriqMultiselect(
          label="Type"
          v-model="datatype"
          :disabled="isSaving"
          :options="availableFields"
          track-by="type"
          display="description"
          :error="showError('datatype', 'Type')"
          :allow-empty="false"
          :on-close="$v.datatype.$touch"
          deselect-label=""
          required="required"
          data-test-id="type"
        )
        BriqCheckbox(label="Optional" v-model="optional" :disabled="isSaving" data-test-id="custom-field-optional")
  Spinner(v-if="isLoading")
</template>

<script>
import { sortBy, compose, toLower, prop } from 'ramda'
import { mapActions } from 'vuex'
import { maxLength, required } from 'vuelidate/lib/validators'
import customFieldTypes from '@v/settings/application/custom-fields/customFieldTypes'
import CustomFieldsService from '@v/settings/application/custom-fields/CustomFieldsService'
import Spinner from '@v/components/Spinner.vue'
import UpsertPage from '@v/components/UpsertPage.vue'

const onlyLettersNumbersAndUnderscore = value => /^[\w]*$/.test(value)

export default {
  components: { Spinner, UpsertPage },
  data() {
    return {
      isNew: false,
      key: null,
      objectType: 'Product',
      fieldName: null,
      fieldCode: null,
      description: '',
      datatype: null,
      optional: false,
      isLoading: true,
      isSaving: false,
      isMounted: false,
      availableFields: sortBy(compose(toLower, prop('description')))(customFieldTypes),
    }
  },
  validations: {
    fieldName: { required, maxLength: maxLength(100) },
    fieldCode: { required, maxLength: maxLength(100), onlyLettersNumbersAndUnderscore },
    datatype: { required },
  },
  methods: {
    ...mapActions('customFields', ['fetchCustomFieldByKey', 'fetchAllCustomFields']),
    toOverview() {
      this.$router.push({ name: 'CustomFieldsOverview' })
    },
    save() {
      if (this.isSaving) return
      this.isSaving = true
      const model = this.createSaveModel()
      this.createOrUpdate(model)
        .then(() => {
          this.fetchAllCustomFields()
          this.toOverview()
          this.$notifySuccess('Custom field saved.')
        })
        .catch(error => {
          this.$logError('There was an error: ', error.response)
          this.$notifyError('Custom field could not be saved.')
        })
        .finally(() => {
          this.isSaving = false
        })
    },
    createSaveModel() {
      return {
        key: this.key,
        objectType: this.objectType,
        fieldName: this.fieldName,
        fieldCode: this.fieldCode,
        description: this.description,
        datatype: this.datatype,
        optional: this.optional,
      }
    },
    createOrUpdate(model) {
      const save = this.isNew
        ? CustomFieldsService.createCustomField
        : CustomFieldsService.editCustomField
      return save(model)
    },
    showError(property, name) {
      if (!this.$v[property] || !this.$v[property].$error) return null
      return `${name} must be valid`
    },
  },
  computed: {
    title() {
      return `${this.isNew ? 'New' : 'Edit'} custom field`
    },
    canSave() {
      return this.isMounted && !this.$v.$invalid
    },
  },
  created() {
    this.isNew = !this.$route.params.customFieldKey
    if (this.isNew) {
      this.isLoading = false
      return
    }

    this.fetchCustomFieldByKey(this.$route.params.customFieldKey)
      .then(customField => {
        this.key = customField.key
        this.fieldName = customField.fieldName
        this.fieldCode = customField.fieldCode
        this.description = customField.description
        this.datatype = customField.datatype
        this.optional = customField.optional
      })
      .catch(error => {
        this.$logError('An error occurred: ', error)
        this.$notifyError(
          `Custom field for key '${this.$route.params.customFieldKey}' could not be found`,
        )
      })
      .finally(() => (this.isLoading = false))
  },
  mounted() {
    this.isMounted = true
    if (!this.isNew) this.$v.$touch()
  },
}
</script>

<style scoped lang="stylus">
.custom-field-edit
  margin 0 auto
</style>

<style lang="stylus">
.custom-field-edit .briq-form-element
  margin-bottom 24px
</style>
